import { useEffect, useState } from 'react';
import axios from 'axios';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Package, ArrowRight } from 'lucide-react';
import { trackMarketingVisit } from '../lib/marketingVisit';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;
// Default fallback image used when the admin has not configured any
// dedicated register-page imagery.
const DEFAULT_BG = 'https://images.unsplash.com/photo-1583927109257-f21c74dd0c3f?crop=entropy&cs=srgb&fm=jpg&q=85';
const GOOGLE_ONE_TAP_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_ONE_TAP_CLIENT_ID);

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [bgImages, setBgImages] = useState([DEFAULT_BG]);
    const [activeBgIdx, setActiveBgIdx] = useState(0);
    const [slideEnabled, setSlideEnabled] = useState(false);
    const [slideInterval, setSlideInterval] = useState(6);
    const [configLoaded, setConfigLoaded] = useState(false);
    // Partner Programme (present when the user reached us through
    // /register/{slug}). Everything Partner-related is DB-driven; there
    // is no per-programme code path.
    const { slug } = useParams();
    const [programme, setProgramme] = useState(null);
    const [programmeChecked, setProgrammeChecked] = useState(!slug);
    const { register, loginWithGoogleOneTap } = useAuth();
    const { t, language, applyEntryPointDefault } = useTranslation();
    const navigate = useNavigate();

    // Google One Tap — same UX as the login page: shows the FedCM popup
    // automatically when a Client ID is configured and the user is not
    // already signed in. New visitors land on this prompt and can sign
    // up with one click; existing Google users get re-authed silently.
    useGoogleOneTapLogin({
        disabled: !GOOGLE_ONE_TAP_ENABLED || Boolean(localStorage.getItem('token')),
        onSuccess: async (credentialResponse) => {
            const credential = credentialResponse?.credential;
            if (!credential) return;
            try {
                const userData = await loginWithGoogleOneTap(credential);
                toast.success(`Welcome, ${userData.name || userData.email}`);
                navigate('/', { replace: true });
            } catch (err) {
                const d = err?.response?.data?.detail;
                const msg = typeof d === 'string' ? d
                    : Array.isArray(d) ? d.map((e) => e?.msg || String(e)).filter(Boolean).join(' · ')
                    : (d && d.msg) ? String(d.msg)
                    : 'Google sign-in failed';
                toast.error(msg);
            }
        },
        onError: () => {
            // Silently ignore — user can still register with email/password.
        },
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true,
    });

    // Partner-programme metadata — only when /register/{slug} is used.
    // Fails silently: unknown or inactive slug → falls back to plain
    // register (server also silently drops the slug on submit).
    useEffect(() => {
        if (slug) trackMarketingVisit('affinity', slug);
    }, [slug]);

    useEffect(() => {
        if (!slug) return;
        let cancelled = false;
        (async () => {
            try {
                const langQ = language && language !== 'en' ? `?lang=${encodeURIComponent(language)}` : '';
                const res = await axios.get(`${API}/partner-programmes/by-slug/${slug}${langQ}`);
                if (cancelled) return;
                setProgramme(res.data);
                // Apply Affinity Programme default_language (priority 4).
                // No-op if the visitor has a stronger source (user pref,
                // localStorage explicit, company default).
                if (res.data?.default_language) {
                    applyEntryPointDefault(res.data.default_language);
                }
                const urls = (res.data?.right_side_image_urls || [])
                    .map((u) => `${API_BASE}${u}`);
                if (urls.length > 0) {
                    setBgImages(urls);
                    setSlideEnabled(urls.length > 1);
                    setConfigLoaded(true);
                }
            } catch {
                if (!cancelled) setProgramme(null);
            } finally {
                if (!cancelled) setProgrammeChecked(true);
            }
        })();
        return () => { cancelled = true; };
    }, [slug, language]);

    // Fetch register-page-specific image config. Admin can pick which
    // uploaded images appear on Sign in vs. Create account via System
    // Settings → Login Page. Skipped when a partner programme supplied
    // its own dedicated imagery above.
    useEffect(() => {
        if (!programmeChecked) return;
        if (programme && bgImages !== null && bgImages[0] !== DEFAULT_BG) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/auth/login-page-config?page=register`);
                if (cancelled) return;
                const imgs = (res.data?.images || []).map((i) => `${API_BASE}${i.url}`);
                setBgImages(imgs.length > 0 ? imgs : [DEFAULT_BG]);
                setSlideEnabled(Boolean(res.data?.settings?.slideshow_enabled) && imgs.length > 1);
                setSlideInterval(Math.max(2, Math.min(60, Number(res.data?.settings?.slideshow_interval_seconds) || 6)));
            } catch {
                if (!cancelled) setBgImages([DEFAULT_BG]);
            } finally {
                if (!cancelled) setConfigLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programmeChecked]);

    useEffect(() => {
        if (!slideEnabled || bgImages.length < 2) return;
        const t = setInterval(() => {
            setActiveBgIdx((idx) => (idx + 1) % bgImages.length);
        }, slideInterval * 1000);
        return () => clearInterval(t);
    }, [slideEnabled, slideInterval, bgImages.length]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const extras = programme?.slug ? { partner_programme_slug: programme.slug } : {};
            const user = await register(email, password, name, extras);
            toast.success(`Welcome, ${user.name}! Your account has been created.`);
            // Onboarding step-through — both partner and normal signups
            // land here with an ``incomplete`` placeholder org (no
            // account type / country / billing address / VAT id yet).
            // Send them straight into the same Billing Profile form so
            // they can NEVER reach a "Ready to submit" order state
            // before the org is real. Once the profile is complete the
            // page navigates them onward on its own.
            const needsBillingProfile = (
                user?.organization_billing_status === 'incomplete'
                || !user?.active_company_id
            );
            navigate(needsBillingProfile ? '/billing' : '/dashboard');
        } catch (error) {
            // FastAPI/Pydantic 422 responses put an ARRAY of error
            // objects on `detail` (e.g. invalid email). Feeding that
            // straight to toast.error rendered `[{type,loc,msg,…}]`
            // as JSX and crashed React with "Objects are not valid
            // as a React child". Coerce to a readable string first
            // — the first ``msg`` is what a human wants to read.
            const detail = error?.response?.data?.detail;
            let message = 'Registration failed';
            if (typeof detail === 'string' && detail.trim()) {
                message = detail;
            } else if (Array.isArray(detail) && detail.length) {
                message = detail
                    .map((e) => (typeof e === 'string' ? e : e?.msg))
                    .filter(Boolean)
                    .join(' · ') || message;
            } else if (detail && typeof detail === 'object' && detail.msg) {
                message = String(detail.msg);
            }
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen lg:h-screen lg:overflow-hidden flex" data-testid="register-page">
            {/* Left Side - Form */}
            <div className="w-full lg:w-1/2 flex flex-col px-8 py-5 bg-background lg:overflow-y-auto">
                {/* Logo - Top Left. Small fixed gap below the logo so it
                    reads as a separate visual layer from the form, while
                    keeping the whole card visible on a 1366×768 laptop
                    without scrolling. */}
                <div className="flex items-center mb-4 lg:mb-6">
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                </div>

                {/* Form — viewport-centred via flex. With `lg:h-screen`
                    on the parent the whole card fits on 1366×768. */}
                <div className="flex-1 flex items-center justify-center min-h-0">
                    <div className="w-full max-w-md animate-fadeIn">
                        <Card className="border-0 shadow-premium-lg">
                        <CardHeader className="space-y-0.5 pb-3">
                            <CardTitle className="text-2xl font-bold font-display">
                                {programme
                                    ? `Join ${programme.name}`
                                    : t('auth.register', 'Create account')}
                            </CardTitle>
                            <CardDescription>
                                {programme
                                    ? (programme.headline || `Sign up to unlock ${programme.name} member pricing`)
                                    : t('auth.register.subtitle', 'Create your account to get started')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="name">{t('auth.name', 'Full Name')}</Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder="John Doe"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="register-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="name@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="register-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="h-11"
                                        data-testid="register-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium btn-active"
                                    disabled={isLoading}
                                    data-testid="register-submit-button"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="spinner w-4 h-4 border-2"></div>
                                            {t('common.loading', 'Loading...')}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            {t('auth.register', 'Create account')}
                                            <ArrowRight className="h-4 w-4" />
                                        </span>
                                    )}
                                </Button>
                            </form>

                            {/* OR divider */}
                            <div className="relative my-4">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-slate-200" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-white px-3 text-slate-400 font-medium">OR</span>
                                </div>
                            </div>

                            {/* Continue with Google */}
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 font-medium border-slate-300 hover:bg-slate-50 text-slate-700"
                                data-testid="google-register-button"
                                onClick={() => {
                                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                    const redirectUrl = window.location.origin;
                                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                }}
                            >
                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                {t('auth.google', 'Continue with Google')}
                            </Button>

                            <div className="mt-6 text-center">
                                <p className="text-sm text-muted-foreground">
                                    {t('auth.signin.prompt', 'Already have an account?')}{' '}
                                    <Link 
                                        to="/login" 
                                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                                        data-testid="login-link"
                                    >
                                        {t('auth.signin', 'Sign in')}
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    </div>
                </div>
            </div>

            {/* Right Side - Image (with optional slideshow).
                Partner-programme images (when /register/{slug} is used)
                carry the marketing message themselves; we intentionally
                do NOT overlay copy on top of them, per admin request. */}
            <div className="hidden lg:block lg:w-1/2 login-image relative overflow-hidden bg-background" data-testid="register-hero-image">
                {configLoaded && bgImages.map((src, idx) => (
                    <div
                        key={src + idx}
                        className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1200ms] ease-in-out"
                        style={{
                            backgroundImage: `url(${src})`,
                            opacity: idx === activeBgIdx ? 1 : 0,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
